import { Inject } from '@odinjs/odin';
import { BasicFile, FieldAPI, FileUploaderAPI, FormAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnCancel, OnMount, onRemove } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { FragmentNames, MyChartSendNewDocumentAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { FileRuleUtils } from '../../utils/file-rules-utils';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { FileProviderData } from '../../constants/file-provider-enum';

interface FormNewDocumentProps {
  files: BasicFile[];
}

interface DocumentSentProps extends Record<string, Value> {
  filePath: string;
  size: number;
  title: string;
}

enum ContextKeys {
  MANUAL_FORM_CANCELLATION = 'manualFormCancellation',
  IS_SEND_DOCUMENT_ROUTE = 'isSendDocument',
}

@Controller({ fragment: FragmentNames.MY_CHART_SEND_NEW_DOCUMENT })
export default class MyChartSendingDocumentsController extends BaseController {

  @Inject(MyChartSendNewDocumentAliases.FIELD_FILE_UPLOADER)
    fieldFileUploaderAPI!: FieldAPI;

  @Inject(MyChartSendNewDocumentAliases.FORM_NEW_DOCUMENT)
    formNewDocumentAPI!: FormAPI;

  @OnMount(MyChartSendNewDocumentAliases.FILE_UPLOADER)
  async onMountFileUploader(event: MountEvent): Promise<void> {
    const fileUploaderApi = event.target as FileUploaderAPI;
    fileUploaderApi.setFileHandlerProviderName(FileProviderData.fileProvider);

    FileRuleUtils.validateFileUploaderRules(fileUploaderApi);
  }

  @OnMount(MyChartSendNewDocumentAliases.FIELD_FILE_UPLOADER)
  async handleFieldFileUploaderLoading(event: MountEvent): Promise<void> {
    const fieldUploadAPI = event.target as FieldAPI;
    await fieldUploadAPI.validate();
  }

  @onRemove(MyChartSendNewDocumentAliases.FILE_UPLOADER)
  async handleFileUploaderChange(): Promise<void> {
    await this.fieldFileUploaderAPI.validate();
  }

  @OnBeforeSubmit({ target: MyChartSendNewDocumentAliases.FORM_NEW_DOCUMENT })
  async handleFormNewDocumentSubmit(event: AbortableSubmitEvent): Promise<void> {
    if (!(await this.fieldFileUploaderAPI.validate())) {
      event.abort();
      return;
    }

    this.formNewDocumentAPI.setLoading(true);

    const { files } = event.record as FormNewDocumentProps;
    const body = files.map<DocumentSentProps>(file => ({
      filePath: file.filepath,
      size: file.size,
      title: file.description || ''
    })) as unknown as Record<string, Value>;

    const currentRoute = this.historyService.getCurrentRoute();
    const routeSendingDocument = APPLICATION_ROUTES.MY_CHART_SEND_DOCUMENT;
    const routeSendDocument = this.context.get<boolean>(ContextKeys.IS_SEND_DOCUMENT_ROUTE);
    this.context.set({[ContextKeys.IS_SEND_DOCUMENT_ROUTE] : currentRoute.match(routeSendingDocument )})

    HttpService.post(routeSendDocument?APPLICATION_ENDPOINTS.POST_MC_SEND_DOCUMENT:APPLICATION_ENDPOINTS.POST_MC_SENDING_DOCUMENTS,
      { patientId: UserContext.patientId },
      { body }
    ).then(() => {
      this.clearFormNewDocument();
      showToastNotificationSuccess('patient.message.files-uploaded-successfully');

      applicationContext.popNavigationItem();
      this.historyService.push(APPLICATION_ROUTES.MY_CHART_SENDING_DOCUMENTS);
    }).catch(() => {
      showToastNotificationError('newaccount.error.internal.server.error.try.again');
    }).finally(() => {
      this.formNewDocumentAPI.setLoading(false);
    });
  }

  private clearFormNewDocument(): void {
    this.executeManualFormCancellation();
    setTimeout(() => {
      this.formNewDocumentAPI.setEditing(true);
    });
  }

  private executeManualFormCancellation(): void {
    this.formNewDocumentAPI.cancel();
    this.context.set({ [ContextKeys.MANUAL_FORM_CANCELLATION]: true });
  }

  @OnCancel(MyChartSendNewDocumentAliases.FORM_NEW_DOCUMENT)
  handleFormNewDocumentCancel(): void {
    const manualFormCancellation = this.context.get(ContextKeys.MANUAL_FORM_CANCELLATION) as boolean;
    if (manualFormCancellation) {
      this.context.set({ [ContextKeys.MANUAL_FORM_CANCELLATION]: undefined });
      return;
    }

    applicationContext.popNavigationItem();
    this.historyService.push(APPLICATION_ROUTES.MY_CHART_SENDING_DOCUMENTS);
  }
}